import {
  BitmaskTypeCollection,
  createBitmaskCollection,
  getFlagNames,
} from "./bitmaskHelpers";

/**
 * Cuisine Types using Bitmask Integer System
 *
 * This field type uses bitmask integers to efficiently store multiple cuisine selections.
 * Each cuisine is represented by a power of 2, allowing multiple cuisines to be
 * combined in a single integer value.
 *
 * For example:
 * - Non-specific (1) + Indian (2) = 3
 * - Asian (8) + Greek (16) + Italian (64) = 88
 *
 * Use the bitmaskHelpers utility functions to work with these values.
 */

// Define cuisine flag values as constants for better type safety
export const CUISINE_FLAGS = {
  NON_SPECIFIC: 1,
  INDIAN: 2,
  AFRICAN: 4,
  ASIAN: 8,
  GREEK: 16,
  MEXICAN: 32,
  ITALIAN: 64,
  SPANISH: 128,
} as const;

// Type for cuisine flag values
export type CuisineFlag = typeof CUISINE_FLAGS[keyof typeof CUISINE_FLAGS];

// Cuisine definitions for creating the collection
const cuisineDefinitions = [
  {
    value: CUISINE_FLAGS.NON_SPECIFIC,
    name: "Non-specific kitchen",
    machineName: "NonSpecific",
  },
  {
    value: CUISINE_FLAGS.INDIAN,
    name: "Indian kitchen",
    machineName: "Indian",
  },
  {
    value: CUISINE_FLAGS.AFRICAN,
    name: "African kitchen",
    machineName: "African",
  },
  { value: CUISINE_FLAGS.ASIAN, name: "Asian kitchen", machineName: "Asian" },
  { value: CUISINE_FLAGS.GREEK, name: "Greek kitchen", machineName: "Greek" },
  {
    value: CUISINE_FLAGS.MEXICAN,
    name: "Mexican kitchen",
    machineName: "Mexican",
  },
  {
    value: CUISINE_FLAGS.ITALIAN,
    name: "Italian kitchen",
    machineName: "Italian",
  },
  {
    value: CUISINE_FLAGS.SPANISH,
    name: "Spanish kitchen",
    machineName: "Spanish",
  },
];

// Create the Cuisine_Types collection using the helper
export const Cuisine_Types: BitmaskTypeCollection<CuisineFlag> =
  createBitmaskCollection(cuisineDefinitions);

// Helper function specific to cuisines
export const getCuisineNames = (cuisineMask: number): string[] => {
  const flagToNameMap = Object.fromEntries(
    Cuisine_Types.allFlags.map((flag) => [flag, Cuisine_Types[flag].name])
  );
  return getFlagNames(cuisineMask, flagToNameMap);
};
